import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { http } from "../lib/api";
import { fmtTRY, fmtGold } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../components/ui/drawer";
import { toast } from "sonner";
import {
  Gift, Send, Users, Coins, Sparkles, Radio, Trophy, RotateCcw, ChevronLeft, Heart,
  Ticket, MessageCircleHeart, HandHeart, Banknote, GemIcon
} from "lucide-react";

const EVENT_META = {
  title: "Ayşe & Mehmet",
  subtitle: "Düğün · Ciragan Sarayı, İstanbul",
  goal_kurus: 500000_00,
  stream_url: "https://www.youtube.com/embed/jfKfPfyJRdk",
};

const DEFAULT_NAMES = ["Ali Y.", "Fatma D.", "Mehmet K.", "Zeynep A.", "Emre S.", "Ayşegül B.", "Onur T.", "Selin M."];
const DEFAULT_MSGS  = [
  "Mutluluklar dileriz, ömür boyu!",
  "Ellerinize sağlık, çok güzel bir gece 🌙",
  "Uzaktan da olsa sizinleyiz.",
  "Bir yastıkta kocayın!",
  "Yayına bağlandık, tebrikler!",
  "Yüzük çok yakıştı!",
];

// Static catalogs mirror the backend for demo fidelity
const COUPON_CATALOG = [
  { coupon_id: "AB-1G",   partner: "Altınbaş Kuyumcu", label: "1 gr altın kuponu",       value_kurus: 260000 },
  { coupon_id: "AB-CY",   partner: "Altınbaş Kuyumcu", label: "Çeyrek altın kuponu",     value_kurus: 780000 },
  { coupon_id: "CS-100",  partner: "Çiçek Sepeti",     label: "100 ₺ çiçek kuponu",       value_kurus: 10000 },
  { coupon_id: "CS-500",  partner: "Çiçek Sepeti",     label: "500 ₺ çiçek kuponu",       value_kurus: 50000 },
  { coupon_id: "MIG-250", partner: "Migros",            label: "250 ₺ market kuponu",     value_kurus: 25000 },
  { coupon_id: "SPA-1",   partner: "Zen Spa",           label: "Çift kişilik spa kuponu", value_kurus: 150000 },
];
const CAUSE_CATALOG = [
  { cause_id: "losev", name: "LÖSEV" },
  { cause_id: "tema", name: "TEMA Vakfı" },
  { cause_id: "darussafaka", name: "Darüşşafaka Cemiyeti" },
  { cause_id: "unicef", name: "UNICEF Türkiye" },
  { cause_id: "akut", name: "AKUT" },
];

const KIND_META = {
  cash:     { icon: Banknote,           label_key: "cash" },
  gold:     { icon: GemIcon,            label_key: "gold" },
  coupon:   { icon: Ticket,             label_key: "coupon" },
  message:  { icon: MessageCircleHeart, label_key: "message_gift" },
  donation: { icon: HandHeart,          label_key: "donation" },
};

const seedGifts = (t) => {
  const names = (t && t.sample_names) || DEFAULT_NAMES;
  const [n0, n1, n2, n3, n4, n5, n6, n7] = names;
  return [
    { id: "g1", display_name: n0, kind: "gold",    grams_mg: 3000, amount_kurus: 78000,  message: "Bir çeyrek altın", created_at: new Date(Date.now() - 12e4).toISOString() },
    { id: "g2", display_name: n1, kind: "coupon",  partner: "Altınbaş Kuyumcu", coupon_id: "AB-1G", coupon_value_kurus: 260000, amount_kurus: 260000, message: "1 gr altın kuponu", created_at: new Date(Date.now() - 24e4).toISOString() },
    { id: "g3", display_name: n2, kind: "message", text: "Yeni yolculuğunuz aydınlık olsun ✨", amount_kurus: 0, created_at: new Date(Date.now() - 33e4).toISOString() },
    { id: "g4", display_name: n3, kind: "cash",    amount_kurus: 250000, message: "Mutluluklar!", created_at: new Date(Date.now() - 42e4).toISOString() },
    { id: "g5", display_name: "Sürpriz", kind: "donation", cause_name: "LÖSEV", amount_kurus: 100000, message: "Adınıza bağış", created_at: new Date(Date.now() - 56e4).toISOString() },
    { id: "g6", display_name: n5, kind: "coupon",  partner: "Çiçek Sepeti", coupon_id: "CS-500", coupon_value_kurus: 50000, amount_kurus: 50000, message: "Yeni yuvanız için", created_at: new Date(Date.now() - 72e4).toISOString() },
    { id: "g7", display_name: n6, kind: "gold",    grams_mg: 1000, amount_kurus: 26000, message: "1 gram altın", created_at: new Date(Date.now() - 89e4).toISOString() },
    { id: "g8", display_name: n7, kind: "message", text: "Uzaktan da olsa yanınızdayız 💛", amount_kurus: 0, created_at: new Date(Date.now() - 12e5).toISOString() },
  ];
};

const seedChat = (t) => {
  const names = (t && t.sample_names) || DEFAULT_NAMES;
  const msgs  = (t && t.congrats_msgs) || DEFAULT_MSGS;
  return msgs.map((m, i) => ({
    id: `c${i}`,
    display_name: names[i % names.length],
    text: m,
    created_at: new Date(Date.now() - (60 * (6 - i) + i * 12) * 1000).toISOString(),
  }));
};

function LiveStage({ url }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="absolute inset-0">
      {url && !failed ? (
        <iframe title="demo-stream" src={url} allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen className="absolute inset-0 w-full h-full" onError={() => setFailed(true)} />
      ) : (
        <div className="absolute inset-0 grid place-items-center overflow-hidden">
          <div className="absolute inset-0" style={{
            background: "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.25), transparent 60%), radial-gradient(circle at 70% 80%, rgba(212,175,55,0.15), transparent 55%), #0A1128"
          }} />
          {[...Array(12)].map((_, i) => (
            <span key={i} className="absolute text-primary/40 animate-float-up" style={{
              left: `${(i * 8.3) % 100}%`, top: `${(i * 17) % 100}%`,
              animationDelay: `${i * 300}ms`, animationDuration: `${3 + (i % 4)}s`, fontSize: `${12 + (i % 5) * 4}px`,
            }}><Heart className="inline" /></span>
          ))}
          <div className="relative text-center z-10">
            <div className="w-24 h-24 mx-auto rounded-full grid place-items-center bg-primary/10 ring-1 ring-primary/40 pulse-gold">
              <Radio className="w-10 h-10 text-primary" />
            </div>
            <div className="font-serif text-3xl mt-4 gold-text">Ayşe &amp; Mehmet</div>
            <div className="text-xs uppercase tracking-[0.35em] text-muted-foreground mt-2">Canlı sahne · Ciragan Sarayı</div>
          </div>
        </div>
      )}
    </div>
  );
}

function CountdownRing({ seconds, total = 90 }) {
  const pct = Math.max(0, Math.min(1, seconds / total));
  const r = 32; const c = 2 * Math.PI * r;
  return (
    <div className="relative w-20 h-20">
      <svg viewBox="0 0 72 72" className="w-full h-full -rotate-90">
        <circle cx="36" cy="36" r={r} strokeWidth={4} className="stroke-muted fill-none" />
        <circle cx="36" cy="36" r={r} strokeWidth={4} className="stroke-primary fill-none transition-all duration-700"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="font-mono text-xl">{seconds}</div>
      </div>
    </div>
  );
}

function GiftCard({ g, i, t }) {
  const meta = KIND_META[g.kind] || KIND_META.cash;
  const Icon = meta.icon;
  const label = t[meta.label_key] || g.kind;
  const isMoney = g.kind === "cash" || g.kind === "donation";
  const isGold = g.kind === "gold";
  const isCoupon = g.kind === "coupon";
  const isMessage = g.kind === "message";
  return (
    <div className={`p-3 rounded-lg bg-muted/30 flex items-start justify-between gap-3 animate-float-up ${g.by_me ? "ring-1 ring-primary/40" : ""}`}
         style={{ animationDelay: `${i * 30}ms` }} data-testid={`demo-wall-${i}`}>
      <div className="flex items-start gap-3 min-w-0">
        <span className={`w-9 h-9 rounded-full grid place-items-center ring-1 ring-primary/25 shrink-0 ${isGold ? "bg-primary/15 text-primary" : isCoupon ? "bg-amber-500/10 text-amber-400" : isMessage ? "bg-rose-500/10 text-rose-300" : g.kind === "donation" ? "bg-emerald-500/10 text-emerald-300" : "bg-primary/10 text-primary"}`}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="font-serif truncate">{g.display_name}{g.by_me ? " · siz" : ""}</div>
          {isMessage ? (
            <div className="text-sm text-foreground/90 mt-0.5 line-clamp-2">&ldquo;{g.text}&rdquo;</div>
          ) : g.message ? (
            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">&ldquo;{g.message}&rdquo;</div>
          ) : null}
          {isCoupon && (
            <div className="text-[10px] uppercase tracking-wider text-amber-400 mt-1">{t.coupon_from}: {g.partner}</div>
          )}
          {g.kind === "donation" && (
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 mt-1">{t.donation_to}: {g.cause_name}</div>
          )}
        </div>
      </div>
      <div className="text-end shrink-0">
        <div className="text-[10px] uppercase tracking-wider text-primary/70">{label}</div>
        <div className="font-mono text-sm">
          {isGold ? fmtGold(g.grams_mg) : isMessage ? "—" : fmtTRY(g.amount_kurus)}
        </div>
      </div>
    </div>
  );
}

function GiftFlow({ priceKurusPerMg, onCapture, onClose }) {
  const { t } = useI18n();
  const [kind, setKind] = useState("cash");
  const [amountTL, setAmountTL] = useState(500);
  const [grams, setGrams] = useState(1000);
  const [couponId, setCouponId] = useState(COUPON_CATALOG[0].coupon_id);
  const [causeId, setCauseId]   = useState(CAUSE_CATALOG[0].cause_id);
  const [text, setText] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [message, setMessage] = useState("");
  const [phase, setPhase] = useState("form"); // form|lock|paying|done
  const [name, setName] = useState("Demo Misafir");
  const [seconds, setSeconds] = useState(90);
  const expiryRef = useRef(null);

  useEffect(() => {
    if (phase !== "lock") return;
    const tick = () => {
      const remain = Math.max(0, Math.floor((expiryRef.current - Date.now()) / 1000));
      setSeconds(remain);
      if (remain <= 0) { setPhase("form"); toast.warning("Fiyat kilidi süresi doldu."); }
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [phase]);

  const chosenCoupon = COUPON_CATALOG.find(c => c.coupon_id === couponId);
  const chosenCause  = CAUSE_CATALOG.find(c => c.cause_id === causeId);

  const proceed = () => {
    if (kind === "cash" || kind === "donation") {
      if (!amountTL || parseFloat(amountTL) < 10) return toast.error("Min 10 ₺");
      setPhase("paying");
    } else if (kind === "gold") {
      if (!grams || parseInt(grams, 10) < 100) return toast.error("Min 100 mg");
      expiryRef.current = Date.now() + 90 * 1000;
      setSeconds(90); setPhase("lock");
    } else if (kind === "coupon") {
      // no payment; go straight to done
      captureGift();
    } else if (kind === "message") {
      const clean = text.trim();
      if (!clean) return toast.error("Mesaj boş olamaz");
      captureGift();
    }
  };

  const captureGift = () => {
    const gift = {
      id: `local-${Date.now()}`,
      display_name: anonymous ? "Sürpriz" : (name || "Misafir"),
      kind,
      amount_kurus:
        kind === "cash" || kind === "donation" ? Math.round(parseFloat(amountTL) * 100)
        : kind === "gold" ? priceKurusPerMg * parseInt(grams, 10)
        : kind === "coupon" ? chosenCoupon.value_kurus
        : 0,
      grams_mg: kind === "gold" ? parseInt(grams, 10) : 0,
      partner: kind === "coupon" ? chosenCoupon.partner : undefined,
      coupon_id: kind === "coupon" ? chosenCoupon.coupon_id : undefined,
      coupon_value_kurus: kind === "coupon" ? chosenCoupon.value_kurus : 0,
      cause_name: kind === "donation" ? chosenCause.name : undefined,
      text: kind === "message" ? text.slice(0, 280) : undefined,
      message: message.slice(0, 140),
      created_at: new Date().toISOString(),
      by_me: true,
    };
    onCapture(gift);
    setPhase("done");
    setTimeout(() => onClose && onClose(), 1400);
  };

  const confirm = (outcome = "captured") => {
    if (outcome === "failed") { toast.error("Ödeme başarısız (demo)."); setPhase("form"); return; }
    captureGift();
  };

  if (phase === "done") {
    return (
      <div className="p-6 text-center space-y-3" data-testid="demo-gift-done">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 ring-1 ring-primary/40 grid place-items-center pulse-gold">
          <Sparkles className="w-7 h-7 text-primary" />
        </div>
        <div className="font-serif text-2xl">Teşekkürler</div>
        <p className="text-sm text-muted-foreground">Hediyeniz duvara düşüyor.</p>
      </div>
    );
  }
  if (phase === "lock") {
    const total = priceKurusPerMg * parseInt(grams, 10);
    return (
      <div className="p-6 space-y-6" data-testid="demo-gift-lock">
        <div className="flex items-center gap-4">
          <CountdownRing seconds={seconds} />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.lock_countdown}</div>
            <div className="font-serif text-2xl">{fmtGold(parseInt(grams, 10))}</div>
            <div className="font-mono text-sm text-muted-foreground">{fmtTRY(total)} · {(priceKurusPerMg / 100).toFixed(2)} ₺/mg</div>
          </div>
        </div>
        <div className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40">{t.demo_notice}</div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => confirm("failed")} className="rounded-full" data-testid="demo-gift-fail">Demo Fail</Button>
          <Button onClick={() => confirm("captured")} className="rounded-full" data-testid="demo-gift-confirm">{t.confirm_pay}</Button>
        </div>
      </div>
    );
  }
  if (phase === "paying") {
    return (
      <div className="p-6 space-y-5" data-testid="demo-gift-pay">
        <div className="font-serif text-2xl">{fmtTRY(Math.round(parseFloat(amountTL) * 100))}</div>
        {kind === "donation" && (
          <div className="text-xs text-emerald-300 uppercase tracking-wider">{t.donation_to}: {chosenCause?.name}</div>
        )}
        <div className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40">{t.demo_notice}</div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => confirm("failed")} className="rounded-full" data-testid="demo-gift-fail">Demo Fail</Button>
          <Button onClick={() => confirm("captured")} className="rounded-full" data-testid="demo-gift-confirm">{t.confirm_pay}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 space-y-4" data-testid="demo-gift-form">
      <div><Label>{t.display_name}</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="demo-gift-name" /></div>
      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="w-full grid grid-cols-5 gap-1 h-auto p-1">
          <TabsTrigger value="cash" data-testid="demo-tab-cash" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Banknote className="w-4 h-4" />{t.cash}
          </TabsTrigger>
          <TabsTrigger value="gold" data-testid="demo-tab-gold" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <GemIcon className="w-4 h-4" />{t.gold}
          </TabsTrigger>
          <TabsTrigger value="coupon" data-testid="demo-tab-coupon" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Ticket className="w-4 h-4" />{t.coupon}
          </TabsTrigger>
          <TabsTrigger value="message" data-testid="demo-tab-message" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <MessageCircleHeart className="w-4 h-4" />{t.message_gift}
          </TabsTrigger>
          <TabsTrigger value="donation" data-testid="demo-tab-donation" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <HandHeart className="w-4 h-4" />{t.donation}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cash" className="space-y-3 mt-4">
          <Label>{t.amount}</Label>
          <Input type="number" min={10} step={10} value={amountTL} onChange={(e) => setAmountTL(e.target.value)} data-testid="demo-gift-amount" />
          <div className="flex flex-wrap gap-2">
            {[100, 250, 500, 1000, 2500].map(v => (
              <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setAmountTL(v)}>{v} ₺</Button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="gold" className="space-y-3 mt-4">
          <Label>{t.grams} · 1 gr = 1000 mg</Label>
          <Input type="number" min={100} step={100} value={grams} onChange={(e) => setGrams(e.target.value)} data-testid="demo-gift-grams" />
          <div className="flex flex-wrap gap-2">
            {[500, 1000, 2500, 5000].map(v => (
              <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setGrams(v)}>{v / 1000} gr</Button>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">Anlık fiyat: <span className="font-mono">{(priceKurusPerMg / 100).toFixed(2)} ₺/mg</span></div>
        </TabsContent>

        <TabsContent value="coupon" className="space-y-3 mt-4">
          <Label>{t.choose_coupon}</Label>
          <Select value={couponId} onValueChange={setCouponId}>
            <SelectTrigger data-testid="demo-coupon-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {COUPON_CATALOG.map(c => (
                <SelectItem key={c.coupon_id} value={c.coupon_id} data-testid={`demo-coupon-${c.coupon_id}`}>
                  <span className="flex items-center gap-2">
                    <Ticket className="w-3 h-3 text-amber-400" />
                    <span className="font-serif">{c.partner}</span> · {c.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground">{t.coupon_desc} · Değer: <span className="font-mono">{fmtTRY(chosenCoupon.value_kurus)}</span></div>
        </TabsContent>

        <TabsContent value="message" className="space-y-3 mt-4">
          <Label>{t.only_note}</Label>
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} maxLength={280} placeholder="Sizin için özel bir tebrik yazın..." data-testid="demo-message-text" />
          <div className="text-xs text-muted-foreground">{t.message_gift_desc}</div>
        </TabsContent>

        <TabsContent value="donation" className="space-y-3 mt-4">
          <Label>{t.choose_cause}</Label>
          <Select value={causeId} onValueChange={setCauseId}>
            <SelectTrigger data-testid="demo-cause-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CAUSE_CATALOG.map(c => (
                <SelectItem key={c.cause_id} value={c.cause_id} data-testid={`demo-cause-${c.cause_id}`}>
                  <span className="flex items-center gap-2"><HandHeart className="w-3 h-3 text-emerald-300" /> {c.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label>{t.amount}</Label>
          <Input type="number" min={10} step={10} value={amountTL} onChange={(e) => setAmountTL(e.target.value)} data-testid="demo-donation-amount" />
          <div className="flex flex-wrap gap-2">
            {[100, 250, 500, 1000].map(v => (
              <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setAmountTL(v)}>{v} ₺</Button>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">{t.donation_desc}</div>
        </TabsContent>
      </Tabs>

      {kind !== "message" && (
        <div><Label>{t.message}</Label><Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={140} data-testid="demo-gift-message" /></div>
      )}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
        <Label className="text-sm">{t.anonymous}</Label>
        <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="demo-gift-anon" />
      </div>
      <Button onClick={proceed} className="w-full rounded-full h-11" data-testid="demo-gift-continue">
        {kind === "message" ? t.send_message : t.continue}
      </Button>
    </div>
  );
}

export default function Demo() {
  const { t } = useI18n();
  const [gifts, setGifts] = useState(() => seedGifts(t));
  const [chat, setChat] = useState(() => seedChat(t));
  const [text, setText] = useState("");
  const [viewers, setViewers] = useState(142);
  const [openGift, setOpenGift] = useState(false);
  const [price, setPrice] = useState(260);
  const chatContainerRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => { setGifts(seedGifts(t)); setChat(seedChat(t)); }, [t]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await http.get("/gold/price");
        if (alive && data?.kurus_per_mg) setPrice(data.kurus_per_mg);
      } catch { /* noop */ }
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setViewers((v) => {
        const step = Math.floor(Math.random() * 5) - 2;
        return Math.max(120, Math.min(160, v + step));
      });
    }, 3500);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      if (Math.random() > 0.55) return;
      const msgs = (t && t.congrats_msgs) || DEFAULT_MSGS;
      const names = (t && t.sample_names) || DEFAULT_NAMES;
      const m = { id: `sim-${Date.now()}`, display_name: names[Math.floor(Math.random() * names.length)], text: msgs[Math.floor(Math.random() * msgs.length)], created_at: new Date().toISOString() };
      setChat((c) => [...c.slice(-80), m]);
    }, 12000);
    return () => clearInterval(iv);
  }, [t]);

  useEffect(() => {
    const c = chatContainerRef.current;
    if (c) c.scrollTop = c.scrollHeight;
  }, [chat.length]);

  const totals = useMemo(() => {
    const cash = gifts.filter(g => g.kind !== "message").reduce((s, g) => s + (g.amount_kurus || 0), 0);
    const gold = gifts.filter(g => g.kind === "gold").reduce((s, g) => s + (g.grams_mg || 0), 0);
    return { cash_kurus: cash, gold_mg: gold, count: gifts.length };
  }, [gifts]);

  const leaderboard = useMemo(() => {
    const map = new Map();
    gifts.filter(g => g.display_name !== "Sürpriz" && g.kind !== "message").forEach(g => {
      const prev = map.get(g.display_name) || { display_name: g.display_name, total_kurus: 0, count: 0 };
      prev.total_kurus += (g.amount_kurus || 0); prev.count += 1;
      map.set(g.display_name, prev);
    });
    return [...map.values()].sort((a, b) => b.total_kurus - a.total_kurus).slice(0, 5);
  }, [gifts]);

  const goalPct = Math.min(100, Math.round((totals.cash_kurus / EVENT_META.goal_kurus) * 100));

  const sendChat = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setChat((c) => [...c, { id: `me-${Date.now()}`, display_name: "Siz", text: text.slice(0, 280), created_at: new Date().toISOString(), by_me: true }]);
    setText("");
  };

  const addGift = (g) => {
    setGifts((prev) => [g, ...prev]);
    const meta = KIND_META[g.kind];
    setTimeout(() => toast.success(`${g.display_name}: ${g.kind === "gold" ? fmtGold(g.grams_mg) : g.kind === "message" ? (t[meta.label_key] || "Mesaj") : fmtTRY(g.amount_kurus)}`), 250);
  };

  const resetDemo = () => {
    setGifts(seedGifts(t)); setChat(seedChat(t)); setViewers(142);
    toast.success(t.reset_demo || "Reset");
  };

  return (
    <div className="relative">
      <div className="sticky top-16 z-30 bg-primary/95 text-primary-foreground text-xs uppercase tracking-[0.35em] py-1.5 text-center font-medium" data-testid="demo-banner">
        <span className="inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
          {t.demo_badge || "DEMO"} · {t.demo_note || "Demo"}
        </span>
      </div>

      <div className="mx-auto max-w-7xl px-3 sm:px-6 py-6 pb-28 lg:pb-10">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <Button asChild variant="ghost" size="sm" className="gap-1 -ms-2" data-testid="demo-back">
              <Link to="/"><ChevronLeft className="w-4 h-4" /> Ana Sayfa</Link>
            </Button>
            <div className="text-xs uppercase tracking-[0.3em] text-primary/80 mt-2">{t.types?.wedding || "Wedding"}</div>
            <h1 className="font-serif text-3xl sm:text-4xl gold-text">{EVENT_META.title}</h1>
            <p className="text-sm text-muted-foreground">{EVENT_META.subtitle}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-500/10 text-red-400 ring-1 ring-red-500/30" data-testid="demo-live-badge">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> {t.live_now}
            </span>
            <span className="inline-flex items-center gap-1" data-testid="demo-viewers">
              <Users className="w-3 h-3" /> {viewers} {(t.viewers || "").toLowerCase()}
            </span>
            <Button size="sm" variant="ghost" className="gap-1 rounded-full" onClick={resetDemo} data-testid="demo-reset">
              <RotateCcw className="w-3 h-3" /> {t.reset_demo}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <Card className="overflow-hidden border-primary/20">
              <div className="relative aspect-video bg-secondary">
                <LiveStage url={EVENT_META.stream_url} />
              </div>
            </Card>

            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.goal} · {fmtTRY(EVENT_META.goal_kurus)}</div>
                <div className="font-mono text-sm">{goalPct}%</div>
              </div>
              <Progress value={goalPct} className="h-2" />
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <span className="text-muted-foreground">{t.total_cash}: <span className="font-mono text-foreground" data-testid="demo-total-cash">{fmtTRY(totals.cash_kurus)}</span></span>
                <span className="text-muted-foreground">{t.total_gold}: <span className="font-mono text-foreground" data-testid="demo-total-gold">{fmtGold(totals.gold_mg)}</span></span>
                <span className="text-muted-foreground">{t.gift_count}: <span className="font-mono text-foreground" data-testid="demo-total-count">{totals.count}</span></span>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> {t.wall}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {gifts.slice(0, 12).map((g, i) => <GiftCard key={g.id} g={g} i={i} t={t} />)}
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-primary" /> {t.leaderboard}</h2>
              <ol className="space-y-2">
                {leaderboard.map((r, i) => (
                  <li key={r.display_name} className="flex items-center justify-between p-3 rounded-lg bg-muted/30" data-testid={`demo-lb-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-xs w-6 h-6 rounded-full grid place-items-center ${i === 0 ? "bg-primary/20 text-primary ring-1 ring-primary/40" : "bg-muted"}`}>{i + 1}</span>
                      <span className="font-serif">{r.display_name}</span>
                    </div>
                    <div className="text-end">
                      <div className="font-mono text-sm">{fmtTRY(r.total_kurus)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.count} hediye</div>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          </div>

          <div className="lg:col-span-4">
            <Card className="p-0 overflow-hidden h-[560px] flex flex-col sticky top-32">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="font-serif text-xl">{t.chat}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> {viewers}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" ref={chatContainerRef} data-testid="demo-chat-list">
                {chat.map((m) => (
                  <div key={m.id} className="text-sm animate-float-up">
                    <span className={`font-serif me-2 ${m.by_me ? "text-primary" : "text-primary/70"}`}>{m.display_name}</span>
                    <span className="text-foreground/90">{m.text}</span>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={sendChat} className="p-3 border-t flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t.say} maxLength={280} data-testid="demo-chat-input" />
                <Button type="submit" size="icon" className="rounded-full" data-testid="demo-chat-send"><Send className="w-4 h-4" /></Button>
              </form>
            </Card>
          </div>
        </div>

        {/* Single Drawer instance — two triggers below via CSS visibility */}
        <Drawer open={openGift} onOpenChange={setOpenGift}>
          <DrawerContent>
            <DrawerHeader><DrawerTitle className="font-serif text-2xl">{t.give_gift}</DrawerTitle></DrawerHeader>
            <div className="max-h-[80vh] overflow-y-auto">
              <GiftFlow priceKurusPerMg={price} onCapture={addGift} onClose={() => setOpenGift(false)} />
            </div>
          </DrawerContent>
        </Drawer>

        <div className="fixed bottom-0 inset-x-0 z-40 p-3 lg:hidden">
          <Button className="w-full rounded-full h-12 gap-2 shadow-xl" onClick={() => setOpenGift(true)} data-testid="demo-mobile-give-gift">
            <Coins className="w-4 h-4" /> {t.give_gift}
          </Button>
        </div>
        <div className="hidden lg:block fixed bottom-6 end-6 z-40">
          <Button className="rounded-full h-14 px-6 gap-2 shadow-2xl" onClick={() => setOpenGift(true)} data-testid="demo-desktop-give-gift">
            <Coins className="w-5 h-5" /> {t.give_gift}
          </Button>
        </div>
      </div>
    </div>
  );
}
